<template>
  <div v-if="!isDataReady" class="d-flex justify-center align-center" style="height: 100%; width: 100%;">
    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    <span class="ml-3 title">Adatok betöltése...</span>
  </div>

  <v-card v-else class="mainContent theme-back" tile flat :dark="dark">
    <v-card class="midContent theme-back overflow-auto" tile flat>
      <ActionsContainer
          :full-timeline-items="timelineItems"
          :timelineItems="visibleTimelineItems"
          :scrollBar="scrollBarWidth"
          :isPassive="isPassivePeriod(period)"
          :dmrPlayers="dmrPlayers"
      />
      <DmrTeams :gamedata="gamedata" :scrollBar="scrollBarWidth"/>

      <div id="DmrTimelineOuter" class="overflow-y-auto" style="height: 100%; width: 100%;" v-on:scroll="handleScroll">
        <TimelineDmr
            :timelineData="visibleTimelineItems"
            :rejectReasons="rejectReasonsShort"
        />
      </div>
    </v-card>

    <v-row justify="center" class="dialog-container">
      <ApproveDialog :getLabel="getActionLabel"/>
      <RejectDialog :getLabel="getActionLabel" :rejectReasons="rejectReasons"/>
      <MissingDialog :getLabel="getActionLabel" :gamedata="gamedata" :period="period" :getPeriod="getPeriod"/>
    </v-row>

    <v-alert v-show="alertBox" type="error" class="alertBox" transition="slide-y-reverse-transition">
      {{alertText}}
    </v-alert>

    <!-- 80. tétel: a kapcsolatfigyelő jelzése, ha a DMR nem kap friss adatot -->
    <v-snackbar :value="connectionStale" :timeout="-1" top color="orange darken-3">
      {{ connectionStaleText }}
    </v-snackbar>
  </v-card>
</template>

<script>
import DmrTeams from "@/components/dmr/DmrTeams";
import TimelineDmr from "@/components/dmr/TimelineDmr";
import RejectDialog from "@/components/dmr/RejectDialog";
import ApproveDialog from "@/components/dmr/ApproveDialog";
import MissingDialog from "@/components/dmr/MissingDialog";
import ActionsContainer from "@/components/dmr/ActionsContainer";
import eventBus from '@/eventBus';
import CONSTANTS from "@/constants";
import { mapActions, mapGetters } from "vuex";
import dmrConnectionWatchdog from "@/services/dmrConnectionWatchdog";

export default {
  name: "DmrScreen",
  components: {
    DmrTeams, TimelineDmr, RejectDialog, ApproveDialog, MissingDialog, ActionsContainer
  },
  props: {
    dark: Boolean,
    isLoggedin: Boolean,
    gamedata: Object,
    dmrPlayers: Array,
    period: String,
    gamefinished: Boolean,
  },
  data: function () {
    return {
      scrollBarWidth: this.getScrollBarWidth(),
      lazyLoadItems: 50,
      alertBox: false,
      alertText: '',
      rejectReasons: CONSTANTS.DMR.REJECT_REASONS,
      rejectReasonsShort: CONSTANTS.DMR.REJECT_REASONS_SHORT,
      labels: CONSTANTS.DMR.ACTION_LABELS,
      silentDC: false,
      deletedUUIDs: [],
      connectionStale: false,
      connectionStaleReason: null
    }
  },
  computed: {
    ...mapGetters('dmr', ['getProcessedTimeline', 'isLoading']),
    ...mapGetters('game', ['getSubstituteTimeline', 'getTimelineData']),

    isDataReady() {
      return this.gamedata &&
          this.gamedata.team_a_name &&
          this.dmrPlayers &&
          this.dmrPlayers.length > 0;
    },

    timelineItems() {
      const list = this.getProcessedTimeline || [];
      const seenPeriodChanges = new Set();

      return list.filter(item => {
        if (!item) return false;

        // 1. Töröltek szűrése
        const isDeletedRoot = item.deleted == 1 || item.deleted === true;
        const isDeletedParams = item.params && (item.params.deleted == 1 || item.params.deleted === true);
        const isLocallyDeleted = this.deletedUUIDs.includes(item.UUID);

        if (isDeletedRoot || isDeletedParams || isLocallyDeleted) {
          return false;
        }

        // 2. Technikai események (gamedatachange) elrejtése
        if (item.action === 'gamedatachange') {
          return false;
        }

        // 3. Duplikáció szűrés (Period Change Spam Fix)
        if (item.action === 'periodchange') {
          const tTime = String(item.totalGameTime || 0);
          const pVal = String(item.period || '');
          const uniqueKey = `${tTime}-${pVal}`;

          if (seenPeriodChanges.has(uniqueKey)) {
            return false;
          }
          seenPeriodChanges.add(uniqueKey);
        }

        return true;
      });
    },

    visibleTimelineItems() {
      return this.timelineItems.slice(0, this.lazyLoadItems);
    },

    connectionStaleText() {
      return this.connectionStaleReason === 'no-server-ping'
          ? 'Megszakadt a kapcsolat a szerverrel. Újracsatlakozás...'
          : 'Nem érkezik friss adat a jegyzőkönyvtől. Újracsatlakozás...';
    }
  },
  watch: {
    isDataReady: {
      handler(ready) {
        if (ready) {
          this.initDmr();
        }
      },
      immediate: true
    }
  },
  created() {
    let self = this;
    if (!self.isLoggedin) {
      self.$router.push("/login");
      return;
    }

    // JAVÍTÁS (80. tétel - 32021425644): kapcsolatfigyelő - ha a DMR nem kap
    // friss adatot (elhalt kapcsolat, vagy a CR órája jár, de nem jön
    // óraszinkron), jelez és újracsatlakozik (l. services/dmrConnectionWatchdog.js).
    dmrConnectionWatchdog.start({
      owner: this,
      isClockRunning: () => !!(self.$store.state.clock && self.$store.state.clock.isPaused === false),
      onStatusChange: (stale, info) => {
        self.connectionStale = stale;
        self.connectionStaleReason = stale && info ? info.reason : null;
      }
    });

    this._connectionChangeHandler = function(value) {
      if (value !== 'ok' && value !== 'sync' && self.silentDC) {
        self.showAlert('A kapcsolat megszakadt.');
      }
    };
    eventBus.$on(CONSTANTS.EVENTS.CHANGE.CONNECTION, this._connectionChangeHandler);

    // --- SOCKET LISTENERS ---

    this._dmrPushHandler = data => {
      console.log(`[DMRPUSH-RECEIVED] ID: ${data.UUID || data.id} | action: ${data.action} | params.selectedPlayer: ${data.params && data.params.selectedPlayer} | params.approved: ${data.params && data.params.approved} | params.rejectReason: ${data.params && data.params.rejectReason} | top.approved: ${data.approved} | top.rejectReason: ${data.rejectReason} | clientTime: ${data.clientTime} | partialUpdate: ${data.partialUpdate}`);

      // JAVÍTÁS: a `partialUpdate:true` csak egy ÁTMENETI jelző a KÜLDŐ
      // (jellemzően DMR saját maga, vagy CR) adott kimenő üzenetén - NEM a
      // rekord állandó tulajdonsága. Ha itt, a FOGADÁSKOR nem töröljük,
      // "ráragad" a helyi Vuex/IndexedDB példányra, és minden KÉSŐBBI, akár
      // erről a rekordról induló szerkesztés/mentés is tévesen csonka,
      // csak-jóváhagyás-mezős csomagként menne ki (l. dbmanager.js sync()
      // partialUpdate-ága) - lásd a ReportScreen.vue-ban lévő azonos javítás
      // részletes indoklását.
      delete data.partialUpdate;

      self.handleDmrPush(data);

      const isDeleted =
          (data.deleted == 1 || data.deleted === true) ||
          (data.params && (data.params.deleted == 1 || data.params.deleted === true));

      if (isDeleted) {
        console.log('DMRPUSH identified as DELETE -> Cleaning up:', data.UUID || data.id);
        self.handleDmrDelete(data);
      } else {
        if (window.dbmanager && window.dbmanager.update) {
          const list = Array.isArray(data) ? data : [data];
          list.forEach(item => {
            try {
              let record = JSON.parse(JSON.stringify(item));
              // JAVÍTÁS: dontsync=true (nem false)! A dbmanager.update(value, dontsync)
              // worker-oldali kezelője a dontsync-ből számolja a syncDone/synced
              // mezőket (value.syncDone = Boolean(dontsync)) - false esetén MINDEN
              // egyes, a CR-től kapott (már réges-rég szinkronban lévő) eseményt
              // "szinkronizálatlannak" jelölt a DMR saját másolatán, ezért a DMR
              // folyamatosan, feleslegesen visszaküldte a szervernek a CR MINDEN
              // eseményét sajátjaként - ez versenyhelyzetet okozott, különösen
              // DC/RC körül, amikor a DMR-nél felgyülemlett "szinkronizálatlan"
              // (valójában már kész) események egy csomóban, tömegesen mentek
              // vissza. A CR saját DMRPUSH-kezelője (ReportScreen.vue) helyesen
              // dontsync=true-t használ - ugyanezt kell tennie a DMR-nek is,
              // hiszen ő is csak egy már szinkronban lévő eseményt tükröz vissza.
              window.dbmanager.update(record, true);
            } catch (e) {
              console.error('Error saving DMRPUSH item to DB:', e);
            }
          });
        }
      }
    };
    window.vueio.on(CONSTANTS.SOCKET.ACTIONS.DMRPUSH, this._dmrPushHandler);

    const deleteAction = CONSTANTS.SOCKET.ACTIONS.DELETE || 'delete';
    this._deleteAction = deleteAction;
    this._deleteHandler = data => {
      self.handleDmrDelete(data);
    };
    window.vueio.on(deleteAction, this._deleteHandler);

    this._removeRecordHandler = data => {
      self.handleDmrDelete(data);
    };
    window.vueio.on('remove_record', this._removeRecordHandler);

    this._syncHandler = data => {
      // Itt már nem figyeljük a periodchange-et, mert a Period.vue csinálja!
      if (data.type === 'delete' || data.type === 'remove' || data.type === 'remove_record') {
        self.handleDmrDelete(data.content || data);
      }
    };
    window.vueio.on(CONSTANTS.SOCKET.ACTIONS.SYNC, this._syncHandler);

    this._clockTickHandler = function () {
      if (window.stopwatch) {
        const allEvents = self.$store.state.game.timelineData || [];
        self.calculatePlaytime({
          items: allEvents,
          currentTotalTime: window.stopwatch.getTotalTime(),
          saveToDb: false
        });
      }
    };
    eventBus.$on(CONSTANTS.EVENTS.CLOCK.TICK, this._clockTickHandler);

    this._periodEndsHandler = function () {
      const allEvents = self.$store.state.game.timelineData || [];
      self.calculatePlaytime({
        items: allEvents,
        saveToDb: true
      });
    };
    eventBus.$on(CONSTANTS.EVENTS.PERIOD.ENDS, this._periodEndsHandler);

    this._sendMissingHandler = function (item) {
      self.handleDmrPush(item);
    };
    eventBus.$on(CONSTANTS.DMR.SEND_MISSING, this._sendMissingHandler);

    this._alertboxHandler = function (text) {
      self.showAlert(text);
    };
    eventBus.$on(CONSTANTS.EVENTS.ALERTBOX, this._alertboxHandler);

    this._approveHandler = function (item) {
      const updates = { approved: true, rejectReason: null, message: null, checked: false };
      self.updateRecord(item.UUID, updates);
      self.$store.commit('dmr/UPDATE_ITEM', { uuid: item.UUID, updates });
    };
    eventBus.$on(CONSTANTS.DMR.APPROVE, this._approveHandler);

    this._rejectHandler = function (item) {
      const updates = { approved: false, rejectReason: item.rejectReason, message: item.message, checked: false };
      self.updateRecord(item.UUID, updates);
      self.$store.commit('dmr/UPDATE_ITEM', { uuid: item.UUID, updates });
    };
    eventBus.$on(CONSTANTS.DMR.REJECT, this._rejectHandler);

    this._approveAllHandler = function () {
      const list = self.getProcessedTimeline;
      list.forEach(obj => {
        if (obj.checked) {
          const updates = { approved: true, rejectReason: null, message: null, checked: false };
          self.updateRecord(obj.UUID, updates);
          self.$store.commit('dmr/UPDATE_ITEM', { uuid: obj.UUID, updates });
        }
      });
    };
    eventBus.$on(CONSTANTS.DMR.APPROVE_ALL, this._approveAllHandler);

    this._selectHandler = function (totalGameTime) {
      const list = self.getProcessedTimeline;
      list.forEach(obj => {
        if(obj.action !== 'periodchange' && totalGameTime === obj.totalGameTime && !obj.approved && !obj.rejectReason && !obj.draft) {
          self.$store.commit('dmr/UPDATE_ITEM', { uuid: obj.UUID, updates: { checked: !obj.checked } });
        }
      });
    };
    eventBus.$on(CONSTANTS.DMR.SELECT, this._selectHandler);

    this._selectAllHandler = function () {
      const list = self.getProcessedTimeline;
      list.forEach(obj => {
        if(obj.action !== 'periodchange' && !obj.approved && !obj.rejectReason && !obj.draft) {
          self.$store.commit('dmr/UPDATE_ITEM', { uuid: obj.UUID, updates: { checked: true } });
        }
      });
    };
    eventBus.$on(CONSTANTS.DMR.SELECT_ALL, this._selectAllHandler);
  },
  beforeDestroy() {
    dmrConnectionWatchdog.stop(this);

    // Socket listener cleanup
    if (this._dmrPushHandler) window.vueio.off(CONSTANTS.SOCKET.ACTIONS.DMRPUSH, this._dmrPushHandler);
    if (this._deleteHandler) window.vueio.off(this._deleteAction, this._deleteHandler);
    if (this._removeRecordHandler) window.vueio.off('remove_record', this._removeRecordHandler);
    if (this._syncHandler) window.vueio.off(CONSTANTS.SOCKET.ACTIONS.SYNC, this._syncHandler);

    // EventBus listener cleanup
    if (this._connectionChangeHandler) eventBus.$off(CONSTANTS.EVENTS.CHANGE.CONNECTION, this._connectionChangeHandler);
    if (this._clockTickHandler) eventBus.$off(CONSTANTS.EVENTS.CLOCK.TICK, this._clockTickHandler);
    if (this._periodEndsHandler) eventBus.$off(CONSTANTS.EVENTS.PERIOD.ENDS, this._periodEndsHandler);
    if (this._sendMissingHandler) eventBus.$off(CONSTANTS.DMR.SEND_MISSING, this._sendMissingHandler);
    if (this._alertboxHandler) eventBus.$off(CONSTANTS.EVENTS.ALERTBOX, this._alertboxHandler);
    if (this._approveHandler) eventBus.$off(CONSTANTS.DMR.APPROVE, this._approveHandler);
    if (this._rejectHandler) eventBus.$off(CONSTANTS.DMR.REJECT, this._rejectHandler);
    if (this._approveAllHandler) eventBus.$off(CONSTANTS.DMR.APPROVE_ALL, this._approveAllHandler);
    if (this._selectHandler) eventBus.$off(CONSTANTS.DMR.SELECT, this._selectHandler);
    if (this._selectAllHandler) eventBus.$off(CONSTANTS.DMR.SELECT_ALL, this._selectAllHandler);
  },
  methods: {
    ...mapActions('dmr', ['initDmr', 'handleDmrPush']),
    ...mapActions('substitute', ['calculatePlaytime']),

    getScrollBarWidth () {
      let outer = document.createElement('div');
      outer.style.visibility = 'hidden';
      outer.style.width = '100px';
      outer.style.msOverflowStyle = 'scrollbar';
      document.body.appendChild(outer);
      let widthNoScroll = outer.offsetWidth;
      outer.style.overflow = 'scroll';
      let inner = document.createElement('div');
      inner.style.width = '100%';
      outer.appendChild(inner);
      let widthWithScroll = inner.offsetWidth;
      outer.parentNode.removeChild(outer);
      return widthNoScroll - widthWithScroll;
    },
    handleScroll (e) {
      const element = e ? e.target : document.getElementById("DmrTimelineOuter");

      if(element && element.scrollTop >= (element.scrollHeight - element.offsetHeight)) {
        if(this.timelineItems.length > this.lazyLoadItems) {
          this.lazyLoadItems += 25;
        }
      }
    },
    getActionLabel(action) {
      return this.labels[action];
    },
    getPeriod(period) {
      if (!this.gamedata.periods) return {};
      return this.gamedata.periods.find(p => p.value === period) || {};
    },
    isPassivePeriod(period) {
      return ['zero', 'extendedzero'].includes(this.getPeriod(period).type);
    },

    handleDmrDelete(data) {
      let id = null;
      if (typeof data === 'string') {
        id = data;
      } else if (typeof data === 'object' && data !== null) {
        id = data.UUID || data.id || data.content;
      }

      if (!id) return;

      console.log('DMR: Deleting record', id);
      const state = this.$store.state;

      if (!this.deletedUUIDs.includes(id)) {
        this.deletedUUIDs.push(id);
      }

      this.$store.commit('game/REMOVE_TIMELINE_EVENT', id);
      this.$store.commit('dmr/REMOVE_ITEM', id);
      this.$store.dispatch('dmr/reCalculateScores');
      this.$store.dispatch('game/reCalculateScores', { totalGameTime: 0, sync: false });

      if (window.dbmanager && window.dbmanager.remove) {
        window.dbmanager.remove(id);
      }
      this.$forceUpdate();
    },

    saveAndSend(recordToUpdate, updates) {
      // Megkeressük az eredeti, teljes eseményt a state-ből (mert a recordToUpdate csak egy lecsupaszított DMR UI elem!)
      let originalEvent = this.$store.state.game.timelineData.find(e => e.UUID === recordToUpdate.UUID);
      if (!originalEvent) {
          originalEvent = recordToUpdate; // Fallback, de elvileg mindig meg kell lennie
      }

      let cleanRecord = JSON.parse(JSON.stringify(originalEvent));
      let updatedRecord = cleanRecord;

      Object.assign(updatedRecord, updates);
      if (!updatedRecord.params) updatedRecord.params = {};
      Object.assign(updatedRecord.params, updates);

      if (updates.approved) {
        updatedRecord.isDraft = false;
        updatedRecord.draft = false;
        updatedRecord.params.isDraft = false;
        updatedRecord.params.draft = false;
      }

      let now = window.ts ? window.ts.now() : Date.now();
      if (updatedRecord.clientTime && now <= updatedRecord.clientTime) {
        now = updatedRecord.clientTime + 10;
      }

      // JAVÍTÁS: Megőrizzük az eredeti létrehozási időt a 'date' mezőben,
      // mielőtt a clientTime-ot felülírnánk a szinkronizációs időbélyeggel.
      // Ez biztosítja, hogy a DMR timeline rendezése az eredeti idő alapján történjen.
      if (!updatedRecord.date) {
        updatedRecord.date = updatedRecord.clientTime;
      }

      updatedRecord.clientTime = now;

      updatedRecord.synced = false;
      updatedRecord.syncDone = false;

      // JAVÍTÁS: megjelöljük a HELYI rekordot is `partialUpdate`-nek. Ez a
      // rekord a DMR saját (esetleg elavult tartalmú) helyi másolatát
      // tartalmazza a jóváhagyás-mezőkkel felülírva, és `synced:false`
      // állapotban kerül mentésre - ha DC/RC miatt nem sikerülne azonnal
      // szinkronizálni, a dbmanager.sync() ÁLTALÁNOS, később (pl. egy
      // reconnectkor) újra lefutó mechanizmusa is elküldi majd a szervernek.
      // Enélkül a jelzés nélkül a sync() a TELJES (esetleg elavult tartalmú)
      // rekordot küldené újra, ami pontosan ugyanazt a felülírási hibát
      // okozná, mint amit a saveAndSend saját, közvetlen küldése ellen lentebb
      // már kijavítottunk - csak egy másik, késleltetett útvonalon.
      updatedRecord.partialUpdate = true;

      // Mentés az adatbázisba a TELJES eseménnyel
      window.dbmanager.update(updatedRecord, false);

      // Vuex frissítés
      this.$store.commit('game/UPDATE_TIMELINE_EVENT', updatedRecord);

      // JAVÍTÁS: a szervernek küldött csomag KORÁBBAN az egész `originalEvent`-et
      // (a DMR saját, esetleg ELAVULT helyi példányát) tartalmazta, csak a
      // jóváhagyás-mezőket frissítve rajta. A CR a tartalom (játékos, terület,
      // idő stb.) kizárólagos forrása - a DMR csak jóváhagy/elutasít. Ha a DMR
      // offline volt, amikor a CR időközben javított valamit a tartalomban, a
      // DMR ezzel a "teljes rekordos" visszaküldéssel a saját elavult
      // tartalmával FELÜLÍRTA a CR frissebb javítását a szerveren - és mivel a
      // szerver ezt az adatot változtatás nélkül továbbküldi (broadcast) a
      // CR-nek és a LiveScore-nak is, azok is az elavult tartalmat kapták
      // vissza. A DMR mostantól KIZÁRÓLAG a saját hatáskörébe tartozó
      // jóváhagyás-mezőket küldi el - a `partialUpdate` jelzővel a szerver
      // tudja, hogy ezt a `params`-t mezőnként (nem a teljes `params` objektum
      // cseréjével) kell alkalmaznia, hogy a többi tartalom-mező érintetlen
      // maradjon.
      let socketPayload = {
        UUID: updatedRecord.UUID,
        partialUpdate: true,
        clientTime: now,
        approved: updates.approved,
        rejectReason: updates.rejectReason,
        message: updates.message,
        checked: updates.checked,

        isDraft: updatedRecord.isDraft,
        draft: updatedRecord.draft,

        params: {
          approved: updates.approved,
          rejectReason: updates.rejectReason,
          message: updates.message,
          checked: updates.checked,
          isDraft: updatedRecord.params.isDraft,
          draft: updatedRecord.params.draft
        }
      };

      if (window.vueio && window.vueio.connected) {
        window.vueio.emit(CONSTANTS.SOCKET.ACTIONS.STORE, socketPayload);
      }

      setTimeout(() => {
        if(window.dbmanager && !window.dbmanager.isSyncDone) {
          window.dbmanager.sync();
        }
      }, 500);
    },

    updateRecord(id, updates) {
      const state = this.$store.state;
      let recordToUpdate = null;

      if (state.game && state.game.timelineData) {
        recordToUpdate = state.game.timelineData.find(r => r.UUID === id);
      }

      if (!recordToUpdate && state.game && state.game.dmrTimeline) {
        recordToUpdate = state.game.dmrTimeline.find(r => r.UUID === id);
      }

      if (recordToUpdate) {
        this.saveAndSend(recordToUpdate, updates);
      } else {
        window.dbmanager.read(id).then(dbRecord => {
          if (dbRecord) {
            this.saveAndSend(dbRecord, updates);
          } else {
            console.error('CRITICAL: Record NOT found in DB or Store with UUID:', id);
          }
        }).catch(err => {
          console.error('Error reading record from DB:', err);
        });
      }
    },

    showAlert(text) {
      this.alertBox = true;
      this.alertText = text;
      setTimeout(() => { this.alertBox = false; }, 3500);
    }
  }
}
</script>

<style scoped>
.mainContent {
  height: 100%;
  width: 100%;
  display: flex;
  flex-flow: row;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: nowrap;
}

.midContent {
  height: 100%;
  width: 100%;
  flex-shrink: 1;
  display: flex;
  flex-flow: column;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: nowrap;
  position: relative;
}

.alertBox {
  position: fixed;
  z-index: 1010;
  bottom: 25px;
  left: calc(50% - 350px);
  width: 700px;
}
</style>
